import {
  ClientSideRowModelModule,
  GridApi,
  GridOptions,
  IDatasource,
  IGetRowsParams,
  InfiniteRowModelModule,
  ModuleRegistry,
  NumberEditorModule,
  RowAutoHeightModule,
  RowStyleModule,
  TextEditorModule,
  ValidationModule,
  createGrid,
} from 'ag-grid-community'
import {
  CellSelectionModule,
  ClipboardModule,
  LicenseManager,
  RowGroupingModule,
  RowNumbersModule,
} from 'ag-grid-enterprise'

import { useEffect, useMemo } from 'react'

LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_KEY ?? '')
ModuleRegistry.registerModules([
  InfiniteRowModelModule,
  ClientSideRowModelModule,
  ClipboardModule,
  RowGroupingModule,
  RowNumbersModule,
  RowStyleModule,
  TextEditorModule,
  NumberEditorModule,
  ValidationModule,
  RowAutoHeightModule,
  CellSelectionModule,
])

let gridApi: GridApi<any>

interface Props {
  columnDefs: any
  rowData: any
  fileContainsHeaderRow?: boolean
}

const InfiniteTable = ({ columnDefs, rowData, fileContainsHeaderRow }: Props) => {
  const gridOptions: GridOptions<any> = useMemo(() => {
    return {
      columnDefs: columnDefs,
      defaultColDef: {
        flex: 1,
        minWidth: 100,
        sortable: false,
      },
      rowClassRules: {
        'header-row': (params) => (fileContainsHeaderRow ? params.node.rowIndex === 0 : false),
      },
      cellSelection: true,
      rowBuffer: 0,
      // tell grid we want virtual row model type
      rowModelType: 'infinite',
      // how big each page in our page cache will be, default is 100
      cacheBlockSize: 100,
      // how many extra blank rows to display to the user at the end of the dataset,
      // which sets the vertical scroll and then allows the grid to request viewing more rows of data.
      // default is 1, ie show 1 row.
      cacheOverflowSize: 2,
      // how many server side requests to send at a time. if user is scrolling lots, then the requests
      // are throttled down
      maxConcurrentDatasourceRequests: 1,
      // how many rows to initially show in the grid. having 1 shows a blank row, so it looks like
      // the grid is loading from the users perspective (as we have a spinner in the first col)
      infiniteInitialRowCount: 1000,
      // how many pages to store in cache. default is undefined, which allows an infinite sized cache,
      // pages are never purged. this should be set for large data to stop your browser from getting
      // full of data
      maxBlocksInCache: 10,
      rowNumbers: true,
      // debug: true,
    }
  }, [columnDefs])
  useEffect(() => {
    if (rowData && columnDefs) {
      // setup the grid after the page has finished loading
      const gridDiv = document.querySelector<HTMLElement>('#myGrid')!
      gridApi = createGrid(gridDiv, gridOptions)

      const dataSource: IDatasource = {
        rowCount: undefined, // behave as infinite scroll

        getRows: (params: IGetRowsParams) => {
          // At this point in your code, you would call the server.
          // To make the demo look real, wait for 500ms before returning
          setTimeout(() => {
            // take a slice of the total rows
            const rowsThisPage = rowData?.slice(params.startRow, params.endRow)
            // if on or after the last page, work out the last row.
            let lastRow = -1
            if (rowData?.length <= params.endRow) {
              lastRow = rowData?.length
            }
            // call the success callback
            params.successCallback(rowsThisPage, lastRow)
          }, 500)
        },
      }

      gridApi!.setGridOption('datasource', dataSource)
      return () => {
        gridApi?.purgeInfiniteCache()
        gridApi?.destroy()
        gridDiv.innerHTML = ''
      }
    }
  }, [rowData, columnDefs])
  return <div id='myGrid' style={{ maxHeight: 500, height: '100%', width: '100%' }}></div>
}

export default InfiniteTable
